import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import Layout from "../components/app/Layout";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, ShieldCheck, Clock, Sparkles, AlertTriangle, CreditCard } from "lucide-react";
import MockPaymentModal from "../components/app/MockPaymentModal";

const SECTIONS = [
    { id: "wines", label: "My Wines" },
    { id: "quotes", label: "Quote Requests" },
    { id: "profile", label: "Profile" },
    { id: "premium", label: "Upgrade to Premium" },
];

const STATUS_LABEL = {
    pending: { label: "Pending Review", icon: Clock, color: "#FBBF24" },
    approved: { label: "Approved · Live", icon: ShieldCheck, color: "#C9A961" },
    rejected: { label: "Rejected", icon: X, color: "#EF4444" },
    premium: { label: "Premium · Featured", icon: Sparkles, color: "#C9A961" },
};

export default function WineryDashboard() {
    const { user, loading: authLoading } = useAuth();
    const [section, setSection] = useState("wines");
    const [winery, setWinery] = useState(null);
    const [wines, setWines] = useState([]);
    const [quotes, setQuotes] = useState([]);
    const [editing, setEditing] = useState(null);
    const [showMembershipPaid, setShowMembershipPaid] = useState(false);
    const [activating, setActivating] = useState(false);

    useEffect(() => {
        if (user?.role !== "winery") return;
        api.get("/me/winery").then(({ data }) => setWinery(data)).catch(() => {});
        api.get("/me/wines").then(({ data }) => setWines(data)).catch(() => {});
        api.get("/me/quote-requests").then(({ data }) => setQuotes(data)).catch(() => {});
    }, [user]);

    if (authLoading) return <Layout><div className="px-6 py-32 text-[#71717A]">Loading...</div></Layout>;
    if (!user) return <Navigate to="/login?redirect=/dashboard" replace />;
    if (user.role !== "winery") return <Navigate to="/" replace />;

    const reload = async () => {
        const w = await api.get("/me/wines"); setWines(w.data);
        const q = await api.get("/me/quote-requests"); setQuotes(q.data);
    };

    const status = winery?.status ? STATUS_LABEL[winery.status] : null;

    // Subscription state (mock — backend marks active for 30 days, dLocal swap later)
    const subActive = winery?.subscription_active && winery?.subscription_expires_at && new Date(winery.subscription_expires_at) > new Date();
    const subExpires = subActive ? new Date(winery.subscription_expires_at) : null;
    const daysLeft = subExpires ? Math.max(0, Math.ceil((subExpires - new Date()) / 86400000)) : 0;

    const payMembership = async () => {
        setActivating(true);
        try {
            const { data } = await api.post("/me/winery/subscription/activate");
            setWinery(data);
            setShowMembershipPaid(true);
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setActivating(false);
        }
    };

    return (
        <Layout>
            {/* Subscription banner — global to the dashboard */}
            <div className="px-6 md:px-12 lg:px-16 pt-6">
                {subActive ? (
                    <div className="border border-[#C9A961] bg-[#0A0A0A] px-6 py-4 flex flex-wrap items-center justify-between gap-4" data-testid="subscription-banner-active">
                        <div className="flex items-center gap-3">
                            <ShieldCheck className="w-5 h-5 text-[#C9A961]" />
                            <div>
                                <p className="overline" style={{ color: "#C9A961" }}>Activo</p>
                                <p className="text-sm text-[#E4E4E7] mt-1">
                                    Tu bodega está publicada. Membresía vigente · {daysLeft} día{daysLeft === 1 ? "" : "s"} restantes
                                </p>
                            </div>
                        </div>
                        <button onClick={payMembership} disabled={activating} className="btn-outline !py-2 !px-4 !text-xs" data-testid="renew-membership-btn">
                            Renovar Membresía $2000/mes
                        </button>
                    </div>
                ) : (
                    <div className="border border-[#FBBF24] bg-[#0A0A0A] px-6 py-5 flex flex-wrap items-center justify-between gap-4" data-testid="subscription-banner-inactive">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="w-6 h-6 text-[#FBBF24] shrink-0 mt-1" />
                            <div>
                                <p className="overline" style={{ color: "#FBBF24" }}>Inactivo</p>
                                <p className="text-base text-[#E4E4E7] mt-2 leading-relaxed max-w-2xl">
                                    Tu bodega y vinos <b>no se muestran al público</b> hasta que actives la membresía.
                                    Importadores no pueden encontrarte mientras estés inactivo.
                                </p>
                            </div>
                        </div>
                        <button onClick={payMembership} disabled={activating} className="btn-gold whitespace-nowrap" data-testid="pay-membership-btn">
                            <CreditCard className="w-4 h-4" /> Pagar Membresía $2000/mes
                        </button>
                    </div>
                )}
            </div>

            <div className="px-6 md:px-12 lg:px-16 pt-10 pb-32 grid lg:grid-cols-[260px_1fr] gap-12">
                <aside className="self-start" data-testid="dashboard-sidebar">
                    <p className="overline">Dashboard</p>
                    <h2 className="font-display text-3xl mt-2 mb-2">{winery?.name || "Your Winery"}</h2>
                    {status && (
                        <div className="flex items-center gap-2 mb-10 text-sm" style={{ color: status.color }}>
                            <status.icon className="w-4 h-4" /> {status.label}
                        </div>
                    )}
                    <nav className="flex flex-col gap-1 border-t border-[#27272A] pt-4">
                        {SECTIONS.map((s) => (
                            <button
                                key={s.id}
                                onClick={() => setSection(s.id)}
                                data-testid={`dash-nav-${s.id}`}
                                className={`text-left text-sm tracking-wide py-3 px-2 transition-colors border-l-2 ${section === s.id ? "border-[#C9A961] text-[#C9A961] bg-[#0A0A0A]" : "border-transparent text-[#A1A1AA] hover:text-white"}`}
                            >
                                {s.label}
                            </button>
                        ))}
                    </nav>
                </aside>

                <div data-testid="dashboard-content">
                    {section === "wines" && <WinesSection wines={wines} onAdd={() => setEditing({})} onEdit={setEditing} onChanged={reload} />}
                    {section === "quotes" && <QuotesSection quotes={quotes} onChanged={reload} />}
                    {section === "profile" && winery && <ProfileSection winery={winery} onUpdate={(w) => setWinery(w)} />}
                    {section === "premium" && <PremiumSection />}
                </div>
            </div>

            {editing !== null && <WineForm wine={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
            {showMembershipPaid && (
                <MockPaymentModal
                    title="Membresía Activada"
                    subtitle="Bodega Premium · 30 días"
                    amount={2000}
                    currency="USD"
                    body="Membresía activada por 30 días en modo demostración. Tu bodega y vinos ya están publicados. El equipo de CellarGlobal te contactará por WhatsApp para coordinar la transferencia bancaria real ($2.000 USD/mes)."
                    onClose={() => setShowMembershipPaid(false)}
                    testId="membership-paid-modal"
                />
            )}
        </Layout>
    );
}

function WinesSection({ wines, onAdd, onEdit, onChanged }) {
    const del = async (id) => {
        if (!window.confirm("Delete this wine?")) return;
        try { await api.delete(`/me/wines/${id}`); toast.success("Wine deleted."); onChanged(); }
        catch (e) { toast.error(formatError(e)); }
    };
    return (
        <div>
            <div className="flex items-center justify-between mb-12">
                <h1 className="font-display text-4xl md:text-5xl">My Wines</h1>
                <button onClick={onAdd} className="btn-gold" data-testid="add-wine-btn"><Plus className="w-4 h-4" /> Add Wine</button>
            </div>
            {wines.length === 0 ? (
                <div className="border border-[#27272A] p-16 text-center"><p className="font-display text-3xl text-[#A1A1AA]">No wines yet. Add your first bottle.</p></div>
            ) : (
                <div className="border border-[#27272A]" data-testid="wines-table">
                    <table className="w-full text-sm">
                        <thead className="bg-[#0A0A0A]">
                            <tr className="text-left text-[#71717A] uppercase tracking-wide text-xs">
                                <th className="py-4 px-4">Wine</th>
                                <th className="py-4 px-4">Vintage</th>
                                <th className="py-4 px-4">Grape</th>
                                <th className="py-4 px-4">Stock</th>
                                <th className="py-4 px-4">FOB USD</th>
                                <th className="py-4 px-4">Score</th>
                                <th className="py-4 px-4"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {wines.map((w) => (
                                <tr key={w.id} className="border-t border-[#27272A]" data-testid={`row-wine-${w.id}`}>
                                    <td className="py-4 px-4 font-display text-lg">{w.name}</td>
                                    <td className="py-4 px-4">{w.vintage}</td>
                                    <td className="py-4 px-4">{w.grape}</td>
                                    <td className="py-4 px-4">{w.stock_cases}</td>
                                    <td className="py-4 px-4 text-[#C9A961]">${w.price_fob_usd?.toFixed(2)}</td>
                                    <td className="py-4 px-4">{w.score || "—"}</td>
                                    <td className="py-4 px-4 text-right">
                                        <button onClick={() => onEdit(w)} className="text-[#A1A1AA] hover:text-[#C9A961] mr-3" data-testid={`edit-wine-${w.id}`}><Pencil className="w-4 h-4" /></button>
                                        <button onClick={() => del(w.id)} className="text-[#A1A1AA] hover:text-red-500" data-testid={`delete-wine-${w.id}`}><Trash2 className="w-4 h-4" /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function QuotesSection({ quotes, onChanged }) {
    const [open, setOpen] = useState(null);
    const [reply, setReply] = useState("");

    const send = async () => {
        if (!reply.trim()) return;
        try { await api.patch(`/me/quote-requests/${open.id}`, { reply }); toast.success("Reply saved."); setOpen(null); setReply(""); onChanged(); }
        catch (e) { toast.error(formatError(e)); }
    };

    return (
        <div>
            <h1 className="font-display text-4xl md:text-5xl mb-12">Quote Requests</h1>
            {quotes.length === 0 ? (
                <div className="border border-[#27272A] p-16 text-center"><p className="font-display text-3xl text-[#A1A1AA]">No requests yet.</p></div>
            ) : (
                <div className="border border-[#27272A]" data-testid="quotes-table">
                    <table className="w-full text-sm">
                        <thead className="bg-[#0A0A0A]">
                            <tr className="text-left text-[#71717A] uppercase tracking-wide text-xs">
                                <th className="py-4 px-4">Date</th>
                                <th className="py-4 px-4">Importer</th>
                                <th className="py-4 px-4">Company</th>
                                <th className="py-4 px-4">Country</th>
                                <th className="py-4 px-4">Wine</th>
                                <th className="py-4 px-4">Cases</th>
                                <th className="py-4 px-4">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {quotes.map((q) => (
                                <tr key={q.id} className="border-t border-[#27272A] hover:bg-[#0A0A0A] cursor-pointer" onClick={() => { setOpen(q); setReply(q.reply || ""); }} data-testid={`row-quote-${q.id}`}>
                                    <td className="py-4 px-4 text-[#A1A1AA]">{new Date(q.created_at).toLocaleDateString()}</td>
                                    <td className="py-4 px-4">{q.importer_name}</td>
                                    <td className="py-4 px-4">{q.importer_company}</td>
                                    <td className="py-4 px-4">{q.country}</td>
                                    <td className="py-4 px-4 font-display text-lg">{q.wine_name}</td>
                                    <td className="py-4 px-4">{q.quantity}</td>
                                    <td className="py-4 px-4">
                                        <span className={`text-xs uppercase tracking-wide ${q.status === "new" ? "text-[#C9A961]" : "text-[#71717A]"}`}>{q.status}</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {open && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="quote-detail-modal">
                    <div className="bg-[#0A0A0A] border border-[#27272A] max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between p-8 border-b border-[#27272A]">
                            <div>
                                <p className="overline">From {open.country}</p>
                                <h3 className="font-display text-3xl mt-2">{open.importer_name}</h3>
                                <p className="text-sm text-[#A1A1AA]">{open.importer_company} · {open.importer_email}</p>
                            </div>
                            <button onClick={() => setOpen(null)} className="text-[#71717A] hover:text-white"><X /></button>
                        </div>
                        <div className="p-8 space-y-6">
                            <div><p className="text-xs text-[#71717A] uppercase tracking-wide mb-2">Wine</p><p className="font-display text-2xl">{open.wine_name}</p></div>
                            <div><p className="text-xs text-[#71717A] uppercase tracking-wide mb-2">Quantity</p><p>{open.quantity} cases</p></div>
                            <div><p className="text-xs text-[#71717A] uppercase tracking-wide mb-2">Message</p><p className="text-[#E4E4E7] whitespace-pre-wrap">{open.message || "—"}</p></div>
                            <div className="pt-6 border-t border-[#27272A]">
                                <label className="overline block mb-3">Your Reply</label>
                                <textarea className="input-line resize-none" rows="5" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type your offer, terms, lead time..." data-testid="quote-reply-input" />
                                <button onClick={send} className="btn-gold mt-6 w-full" data-testid="quote-reply-send">Send Reply</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function ProfileSection({ winery, onUpdate }) {
    const [form, setForm] = useState({
        name: winery.name, region: winery.region || "", established_year: winery.established_year || "",
        story: winery.story || "", logo_url: winery.logo_url || "", banner_url: winery.banner_url || "",
    });
    const save = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...form };
            if (form.established_year) payload.established_year = Number(form.established_year);
            else payload.established_year = null;
            const { data } = await api.patch("/me/winery", payload);
            onUpdate(data);
            toast.success("Profile updated.");
        } catch (err) { toast.error(formatError(err)); }
    };
    return (
        <form onSubmit={save} className="max-w-2xl" data-testid="profile-form">
            <h1 className="font-display text-4xl md:text-5xl mb-12">Winery Profile</h1>
            <div className="space-y-8">
                <FieldEdit label="Winery Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="profile-name" />
                <FieldEdit label="Region" value={form.region} onChange={(v) => setForm({ ...form, region: v })} testid="profile-region" />
                <FieldEdit label="Established Year" type="number" value={form.established_year} onChange={(v) => setForm({ ...form, established_year: v })} testid="profile-year" required={false} />
                <FieldEdit label="Logo URL" value={form.logo_url} onChange={(v) => setForm({ ...form, logo_url: v })} testid="profile-logo" required={false} />
                <FieldEdit label="Banner URL" value={form.banner_url} onChange={(v) => setForm({ ...form, banner_url: v })} testid="profile-banner" required={false} />
                <div>
                    <label className="overline block mb-2">Story</label>
                    <textarea className="input-line resize-none" rows="6" value={form.story} onChange={(e) => setForm({ ...form, story: e.target.value })} data-testid="profile-story" />
                </div>
            </div>
            <button type="submit" className="btn-gold mt-12" data-testid="profile-save">Save Changes</button>
        </form>
    );
}

function PremiumSection() {
    return (
        <div className="max-w-2xl" data-testid="premium-section">
            <p className="overline">Featured Placement</p>
            <h1 className="font-display text-5xl md:text-6xl mt-3 mb-8">Premium Membership</h1>
            <p className="text-lg text-[#A1A1AA] mb-12 leading-relaxed">
                Premium wineries are featured on the homepage, receive priority in search, and gain access to importer analytics.
            </p>
            <div className="border border-[#C9A961] p-12">
                <p className="overline">From</p>
                <p className="font-display text-6xl text-[#C9A961] mt-2">$299<span className="text-2xl text-[#A1A1AA]">/mo</span></p>
                <ul className="mt-10 space-y-3 text-[#E4E4E7]">
                    <li>· Featured on homepage</li>
                    <li>· Priority in marketplace search</li>
                    <li>· Importer engagement analytics</li>
                    <li>· Dedicated account manager</li>
                </ul>
                <button className="btn-gold w-full mt-10" data-testid="premium-cta">Contact Sales</button>
            </div>
        </div>
    );
}

function WineForm({ wine, onClose, onSaved }) {
    const isEdit = !!wine.id;
    const [form, setForm] = useState({
        name: wine.name || "", vintage: wine.vintage || new Date().getFullYear() - 1,
        grape: wine.grape || "Malbec", region: wine.region || "Mendoza",
        photos: wine.photos?.join(", ") || "", tech_sheet_pdf_url: wine.tech_sheet_pdf_url || "",
        stock_cases: wine.stock_cases ?? 0, price_fob_usd: wine.price_fob_usd ?? 0,
        score: wine.score || "", description: wine.description || "",
    });

    const save = async (e) => {
        e.preventDefault();
        const payload = {
            ...form,
            vintage: Number(form.vintage), stock_cases: Number(form.stock_cases),
            price_fob_usd: Number(form.price_fob_usd),
            score: form.score ? Number(form.score) : null,
            photos: form.photos.split(",").map((s) => s.trim()).filter(Boolean),
        };
        try {
            if (isEdit) await api.patch(`/me/wines/${wine.id}`, payload);
            else await api.post("/me/wines", payload);
            toast.success(isEdit ? "Wine updated." : "Wine added.");
            onSaved();
        } catch (err) { toast.error(formatError(err)); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="wine-form-modal">
            <form onSubmit={save} className="bg-[#0A0A0A] border border-[#27272A] max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between p-8 border-b border-[#27272A]">
                    <div>
                        <p className="overline">{isEdit ? "Edit" : "New"} Wine</p>
                        <h3 className="font-display text-3xl mt-2">{form.name || "Untitled"}</h3>
                    </div>
                    <button type="button" onClick={onClose} className="text-[#71717A] hover:text-white"><X /></button>
                </div>
                <div className="p-8 space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                        <FieldEdit label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="wineform-name" />
                        <FieldEdit label="Vintage" type="number" value={form.vintage} onChange={(v) => setForm({ ...form, vintage: v })} testid="wineform-vintage" />
                        <FieldEdit label="Grape" value={form.grape} onChange={(v) => setForm({ ...form, grape: v })} testid="wineform-grape" />
                        <FieldEdit label="Region" value={form.region} onChange={(v) => setForm({ ...form, region: v })} testid="wineform-region" />
                        <FieldEdit label="Stock (cases)" type="number" value={form.stock_cases} onChange={(v) => setForm({ ...form, stock_cases: v })} testid="wineform-stock" />
                        <FieldEdit label="FOB Price USD" type="number" value={form.price_fob_usd} onChange={(v) => setForm({ ...form, price_fob_usd: v })} testid="wineform-price" />
                        <FieldEdit label="James Suckling Score" type="number" value={form.score} onChange={(v) => setForm({ ...form, score: v })} testid="wineform-score" required={false} />
                    </div>
                    <FieldEdit label="Photos (comma-separated URLs)" value={form.photos} onChange={(v) => setForm({ ...form, photos: v })} testid="wineform-photos" required={false} />
                    <FieldEdit label="Tech Sheet PDF URL" value={form.tech_sheet_pdf_url} onChange={(v) => setForm({ ...form, tech_sheet_pdf_url: v })} testid="wineform-pdf" required={false} />
                    <div>
                        <label className="overline block mb-2">Description</label>
                        <textarea className="input-line resize-none" rows="3" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="wineform-description" />
                    </div>
                    <button type="submit" className="btn-gold w-full" data-testid="wineform-submit">{isEdit ? "Save Changes" : "Add Wine"}</button>
                </div>
            </form>
        </div>
    );
}

function FieldEdit({ label, value, onChange, type = "text", testid, required = true }) {
    return (
        <div>
            <label className="overline block mb-2">{label}</label>
            <input required={required} type={type} className="input-line" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
        </div>
    );
}
